import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

// Dark theme color palette
const colors = {
  bg: '#0a0a0a',
  surface: '#171717',
  surfaceHover: '#1f1f1f',
  border: '#262626',
  borderFocus: '#84cc16',
  accent: '#84cc16',
  accentHover: '#65a30d',
  textPrimary: '#fafafa',
  textSecondary: '#a3a3a3',
  textMuted: '#737373',
  error: '#ef4444',
  errorBg: 'rgba(239, 68, 68, 0.1)',
  success: '#22c55e',
  successBg: 'rgba(34, 197, 94, 0.1)',
  warning: '#f59e0b',
  warningBg: 'rgba(245, 158, 11, 0.1)',
  info: '#3b82f6',
  infoBg: 'rgba(59, 130, 246, 0.1)',
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: colors.bg,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  main: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "2rem 1rem",
  },
  card: {
    width: "100%",
    maxWidth: "480px",
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: "12px",
    padding: "2rem",
  },
  logo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    marginBottom: "1.5rem",
  },
  logoIcon: {
    width: "48px",
    height: "48px",
    backgroundColor: colors.accent,
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: colors.textPrimary,
    textAlign: "center",
    marginBottom: "0.5rem",
    letterSpacing: "-0.025em",
  },
  subtitle: {
    fontSize: "0.875rem",
    color: colors.textSecondary,
    textAlign: "center",
    marginBottom: "1.5rem",
    lineHeight: 1.5,
  },
  label: {
    display: "block",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: colors.textPrimary,
    marginBottom: "0.5rem",
  },
  input: {
    width: "100%",
    padding: "0.75rem 1rem",
    fontSize: "1rem",
    fontFamily: "monospace",
    letterSpacing: "1px",
    color: colors.textPrimary,
    backgroundColor: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    boxSizing: "border-box",
  },
  inputFocus: {
    borderColor: colors.accent,
    boxShadow: `0 0 0 3px rgba(132, 204, 22, 0.1)`,
  },
  inputHint: {
    fontSize: "0.75rem",
    color: colors.textMuted,
    marginTop: "0.5rem",
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
  },
  button: {
    width: "100%",
    padding: "0.75rem 1rem",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: colors.bg,
    backgroundColor: colors.accent,
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "background-color 0.2s, transform 0.1s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  outlineButton: {
    width: "100%",
    padding: "0.75rem 1rem",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: colors.textPrimary,
    backgroundColor: "transparent",
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    cursor: "pointer",
    transition: "border-color 0.2s, background-color 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
  },
  alert: {
    padding: "1rem",
    borderRadius: "8px",
    fontSize: "0.875rem",
    marginBottom: "1rem",
    display: "flex",
    alignItems: "flex-start",
    gap: "0.75rem",
  },
  alertError: {
    backgroundColor: colors.errorBg,
    border: `1px solid ${colors.error}`,
    color: colors.error,
  },
  alertSuccess: {
    backgroundColor: colors.successBg,
    border: `1px solid ${colors.success}`,
    color: colors.success,
  },
  alertWarning: {
    backgroundColor: colors.warningBg,
    border: `1px solid ${colors.warning}`,
    color: colors.warning,
  },
  alertInfo: {
    backgroundColor: colors.infoBg,
    border: `1px solid ${colors.info}`,
    color: colors.info,
  },
  resultSection: {
    backgroundColor: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    padding: "1rem",
    marginBottom: "1rem",
  },
  resultRow: {
    marginBottom: "0.75rem",
  },
  resultLabel: {
    fontSize: "0.75rem",
    color: colors.textMuted,
    marginBottom: "0.25rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  resultValue: {
    fontSize: "0.875rem",
    color: colors.textPrimary,
  },
  resultValueMono: {
    fontSize: "0.875rem",
    color: colors.textPrimary,
    fontFamily: "monospace",
    fontWeight: 500,
  },
  badge: {
    display: "inline-block",
    padding: "0.25rem 0.5rem",
    fontSize: "0.75rem",
    fontWeight: 500,
    borderRadius: "4px",
  },
  badgeSuccess: {
    backgroundColor: colors.successBg,
    color: colors.success,
    border: `1px solid ${colors.success}`,
  },
  badgeWarning: {
    backgroundColor: colors.warningBg,
    color: colors.warning,
    border: `1px solid ${colors.warning}`,
  },
  footer: {
    padding: "1.5rem",
    textAlign: "center",
    borderTop: `1px solid ${colors.border}`,
  },
  footerText: {
    fontSize: "0.75rem",
    color: colors.textMuted,
    marginBottom: "0.25rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.25rem",
  },
  footerLink: {
    color: colors.accent,
    textDecoration: "none",
    fontSize: "0.75rem",
  },
  spinner: {
    width: "16px",
    height: "16px",
    border: "2px solid transparent",
    borderTopColor: colors.bg,
    borderRadius: "50%",
    animation: "spin 0.6s linear infinite",
  },
};

// Add keyframes for spinner animation
const spinnerKeyframes = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

function Validacion() {
  const [searchParams] = useSearchParams();
  const [numeroSerie, setNumeroSerie] = useState('');
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [inputFocused, setInputFocused] = useState(false);

  const validarNumeroSerie = useCallback(async (serial) => {
    if (!serial || !serial.trim()) return;

    setLoading(true);
    setResultado(null);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/objetos/validacion/${encodeURIComponent(serial.trim().toUpperCase())}`,
        { method: 'GET' }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setResultado(data);
      } else if (response.status === 404) {
        setError('no_encontrado');
      } else if (response.status === 400) {
        setError('formato_invalido');
      } else {
        setError('error_general');
      }
    } catch (err) {
      console.error('Error al validar:', err);
      setError('error_general');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const serialFromUrl = searchParams.get('serial');
    if (serialFromUrl) {
      setNumeroSerie(serialFromUrl.toUpperCase());
      validarNumeroSerie(serialFromUrl);
    }
  }, [searchParams, validarNumeroSerie]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    validarNumeroSerie(numeroSerie);
  };

  const handleReset = () => {
    setNumeroSerie('');
    setResultado(null);
    setError(null);
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return null;
    try {
      return new Date(fecha).toLocaleDateString('es-MX', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return null;
    }
  };

  return (
    <div style={styles.container}>
      <style>{spinnerKeyframes}</style>

      <div style={styles.main}>
        <div style={styles.card}>
          {/* Logo */}
          <div style={styles.logo}>
            <div style={styles.logoIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.5">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <polyline points="9 12 11 14 15 10" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>

          {/* Title */}
          <h1 style={styles.title}>Validación de Producto</h1>
          <p style={styles.subtitle}>
            Verifica que tu producto LemmonTags sea oficial
          </p>

          {/* Form - Initial state */}
          {!resultado && !error && (
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: "1rem" }}>
                <label style={styles.label}>
                  Número de Serie
                </label>
                <input
                  type="text"
                  value={numeroSerie}
                  onChange={(e) => setNumeroSerie(e.target.value.toUpperCase())}
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setInputFocused(false)}
                  placeholder="Ej: PET-000123-AF39C2"
                  required
                  disabled={loading}
                  style={{
                    ...styles.input,
                    ...(inputFocused ? styles.inputFocus : {}),
                    opacity: loading ? 0.5 : 1,
                  }}
                />
                <div style={styles.inputHint}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4M12 8h.01"/>
                  </svg>
                  <span>Encuentra este número en tu tarjeta, empaque o producto LemmonTags.</span>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !numeroSerie.trim()}
                style={{
                  ...styles.button,
                  ...((loading || !numeroSerie.trim()) ? styles.buttonDisabled : {}),
                }}
              >
                {loading ? (
                  <>
                    <div style={styles.spinner} />
                    Validando...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"/>
                      <path d="M21 21l-4.35-4.35"/>
                    </svg>
                    Validar
                  </>
                )}
              </button>
            </form>
          )}

          {/* Result: Valid product */}
          {resultado && (
            <div>
              <div style={{...styles.alert, ...styles.alertSuccess}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                <div>
                  <div style={{ fontWeight: 500, marginBottom: "0.25rem" }}>Producto Válido</div>
                  <div style={{ fontSize: "0.75rem", opacity: 0.9 }}>
                    Este producto es oficial y está registrado en nuestro sistema.
                  </div>
                </div>
              </div>

              <div style={styles.resultSection}>
                <div style={styles.resultRow}>
                  <div style={styles.resultLabel}>Número de Serie</div>
                  <div style={styles.resultValueMono}>{resultado.numeroSerie}</div>
                </div>

                <div style={styles.resultRow}>
                  <div style={styles.resultLabel}>Tipo</div>
                  <div style={styles.resultValue}>{resultado.tipo}</div>
                </div>

                <div style={styles.resultRow}>
                  <div style={styles.resultLabel}>Estado</div>
                  <div>
                    <span style={{
                      ...styles.badge,
                      ...(resultado.estadoRaw === 'activo' ? styles.badgeSuccess : styles.badgeWarning)
                    }}>
                      {resultado.estado}
                    </span>
                  </div>
                </div>

                {resultado.creadoPara && (
                  <div style={styles.resultRow}>
                    <div style={styles.resultLabel}>Creado para</div>
                    <div style={{...styles.resultValue, color: colors.accent, fontWeight: 500}}>
                      {resultado.creadoPara}
                    </div>
                  </div>
                )}

                {resultado.fechaCreacion && (
                  <div style={styles.resultRow}>
                    <div style={styles.resultLabel}>Fecha de creación</div>
                    <div style={styles.resultValue}>{formatearFecha(resultado.fechaCreacion)}</div>
                  </div>
                )}

                {resultado.autenticidad && (
                  <>
                    {resultado.autenticidad.tipoProducto && (
                      <div style={styles.resultRow}>
                        <div style={styles.resultLabel}>Producto</div>
                        <div style={styles.resultValue}>{resultado.autenticidad.tipoProducto}</div>
                      </div>
                    )}
                    {resultado.autenticidad.edicion && (
                      <div style={styles.resultRow}>
                        <div style={styles.resultLabel}>Edición</div>
                        <div style={styles.resultValue}>{resultado.autenticidad.edicion}</div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div style={{...styles.alert, ...styles.alertInfo, marginBottom: "1rem"}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <path d="M7 7h.01M7 12h.01M7 17h.01M12 7h5M12 12h5M12 17h5"/>
                </svg>
                <span style={{ fontSize: "0.8rem" }}>
                  Para ver los datos de contacto del dueño o reportar un objeto perdido, escanea el código QR del producto.
                </span>
              </div>

              <button
                onClick={handleReset}
                style={styles.outlineButton}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 4v6h6M23 20v-6h-6"/>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                </svg>
                Validar otro producto
              </button>
            </div>
          )}

          {/* Error: Not found */}
          {error === 'no_encontrado' && (
            <div>
              <div style={{...styles.alert, ...styles.alertError}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M15 9l-6 6M9 9l6 6"/>
                </svg>
                <div>
                  <div style={{ fontWeight: 500, marginBottom: "0.25rem" }}>No pudimos validar este producto</div>
                  <div style={{ fontSize: "0.75rem", opacity: 0.9 }}>
                    El número de serie ingresado no existe en nuestra base de datos.
                  </div>
                </div>
              </div>

              <p style={{ fontSize: "0.875rem", color: colors.textSecondary, marginBottom: "1rem" }}>
                Verifica que lo hayas escrito correctamente.
              </p>

              <div style={{...styles.alert, ...styles.alertWarning, marginBottom: "1rem"}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <span style={{ fontSize: "0.8rem" }}>
                  <strong>Importante:</strong> Si este producto pretende ser LemmonTags pero no aparece aquí, no es un producto oficial.
                </span>
              </div>

              <button
                onClick={handleReset}
                style={styles.outlineButton}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 4v6h6M23 20v-6h-6"/>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                </svg>
                Intentar de nuevo
              </button>
            </div>
          )}

          {/* Error: Invalid format */}
          {error === 'formato_invalido' && (
            <div>
              <div style={{...styles.alert, ...styles.alertWarning}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <div>
                  <div style={{ fontWeight: 500, marginBottom: "0.25rem" }}>Número de serie no válido</div>
                  <div style={{ fontSize: "0.75rem", opacity: 0.9 }}>
                    Verifica que hayas escrito correctamente el número de serie.
                  </div>
                </div>
              </div>

              <div style={styles.resultSection}>
                <div style={styles.resultLabel}>Formato esperado</div>
                <div style={{...styles.resultValueMono, marginBottom: "0.5rem"}}>TIPO-000000-ABC123</div>
                <div style={{ fontSize: "0.75rem", color: colors.textMuted }}>
                  Ejemplo: PET-000123-AF39C2
                </div>
              </div>

              <button
                onClick={handleReset}
                style={styles.outlineButton}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 4v6h6M23 20v-6h-6"/>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                </svg>
                Intentar de nuevo
              </button>
            </div>
          )}

          {/* Error: General */}
          {error === 'error_general' && (
            <div>
              <div style={{...styles.alert, ...styles.alertError}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 8v4M12 16h.01"/>
                </svg>
                <span>Error al procesar la solicitud. Por favor intenta nuevamente.</span>
              </div>

              <button
                onClick={handleReset}
                style={styles.outlineButton}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 4v6h6M23 20v-6h-6"/>
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
                </svg>
                Intentar de nuevo
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer style={styles.footer}>
        <p style={styles.footerText}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          <span>Validación de autenticidad LemmonTags</span>
        </p>
        <a href="mailto:soporte@lemmontags.com" style={styles.footerLink}>
          soporte@lemmontags.com
        </a>
      </footer>
    </div>
  );
}

export default Validacion;
